import React, { useEffect, useRef, useState } from "react";
import TokenInput from "./react-tokeninput/main";
import ComboboxOption from "./react-tokeninput/option";
import Language from "../lib/Language";
import API from "../lib/TimeEditAPI";
import _ from "underscore";
import Log from "../lib/Log";
import { getTagOptions, normalizeTag, resolveTagSelection, withoutTags } from "../lib/TagUtils";

const DEBOUNCE_FIND_TAGS_TIME = 200;

const contentElements = (fromValues: string[] = [], selectedTags: string[], currentInput: string) =>
    getTagOptions(fromValues, selectedTags, currentInput).map((value) => (
        <ComboboxOption
            key={value}
            value={value}
            label={value}
            isfocusable="true"
            isselected="false"
        >
            {value}
        </ComboboxOption>
    ));

const selectedTokens = (fromTags: string[] = []) =>
    fromTags.map((tag) => ({
        name: tag,
        id: tag,
    }));

type TTagInputProps = {
    tags?: string[];
    reservationIds?: number[];
};

const TagInput = (props: TTagInputProps) => {
    // Kept as a single state object rather than separate useState calls: every
    // async callback below needs the *current* selectedTags to filter against, and
    // the functional updater form gives that without stale closures or a ref.
    const [state, setState] = useState<{ tags: string[]; selectedTags: string[] }>({
        tags: [],
        selectedTags: [],
    });

    // Not state: the class mutated this instance field without re-rendering, so
    // it is only reflected when some other update repaints. useRef preserves that.
    const currentInput = useRef("");

    const loadMyTags = () => {
        API.findMyTags("", (result) => {
            setState((prev) => ({
                ...prev,
                tags: withoutTags(result.tags, prev.selectedTags),
            }));
        });
    };

    // Replaces componentDidMount + componentDidUpdate, which shared a body. The
    // guard must stay a deep compare: ReservationInfo rebuilds both arrays on
    // every render, so a dependency array would refire this API call constantly.
    const prevInputs = useRef<{ tags?: string[]; reservationIds?: number[] } | null>(null);
    useEffect(() => {
        const prev = prevInputs.current;
        if (
            prev === null ||
            !_.isEqual(prev.tags, props.tags) ||
            !_.isEqual(prev.reservationIds, props.reservationIds)
        ) {
            prevInputs.current = { tags: props.tags, reservationIds: props.reservationIds };
            setState((current) => ({ ...current, selectedTags: props.tags || [] }));
            loadMyTags();
        }
    });

    const debounceFindTags = useRef(
        _.debounce((value) => {
            API.findTags(value, (result) => {
                setState((prev) => ({
                    ...prev,
                    tags: withoutTags(result.tags, prev.selectedTags),
                }));
            });
        }, DEBOUNCE_FIND_TAGS_TIME)
    ).current;

    const onSelect = (input) => {
        const selection = resolveTagSelection(input, state.tags, state.selectedTags);
        if (selection.kind === "empty" || selection.kind === "alreadySelected") {
            return;
        }
        const value = selection.tag;
        if (selection.kind === "new") {
            API.createTag(value, (createResult) => {
                console.log(createResult);
                if (createResult.details) {
                    Log.error(createResult.details);
                    return;
                }
                API.addTagToReservations(props.reservationIds!, value, (result) => {
                    if (result[0].details) {
                        Log.error(result[0].details);
                    }
                });
            });
        } else {
            API.addTagToReservations(props.reservationIds!, value, (result) => {
                if (result[0].details) {
                    Log.error(result[0].details);
                }
            });
        }
        setState((prev) => ({
            selectedTags: [...prev.selectedTags, value],
            tags: prev.tags.filter((tag) => tag !== value),
        }));
    };

    const onRemove = (value) => {
        if (!value) {
            return;
        }
        setState((prev) => ({
            selectedTags: prev.selectedTags.filter((tag) => tag !== value.id),
            tags: [...prev.tags, value.id],
        }));
        API.removeTagFromReservations(props.reservationIds!, value.id, (result) => {
            if (result.details) {
                Log.info(result.details);
            }
        });
    };

    const onInput = (value) => {
        currentInput.current = value;
        // The server's search doesn't trim, so "fm " would not find "Fm".
        debounceFindTags(normalizeTag(value));
    };

    return (
        <TokenInput
            showListOnFocus={true}
            onInput={onInput}
            onSelect={onSelect}
            onRemove={onRemove}
            selected={selectedTokens(state.selectedTags)}
            menuContent={contentElements(state.tags, state.selectedTags, currentInput.current)}
            placeholder={Language.get("nc_tags_type_to_search")}
        />
    );
};

export default TagInput;
